// Phase JA.4 — useUserSlips hook.
//
// Authenticated-only hook that fetches the current user's
// `daily_acca_user_slips` rows and subscribes to realtime updates on
// the same set. Anon users get an empty array and `lockSlip()` calls
// the supplied auth-prompt callback (the page wires this to /signin).
//
// Doctrine carry-forward:
//   - Own-row only. RLS enforces auth.uid() = user_id; the client query
//     also passes user_id explicitly for clarity.
//   - No fabrication. Empty array on no rows.
//   - No stake recommendation. The hook records `stake_amount` only
//     when the caller passes one in (user-typed); otherwise null.

import { useCallback, useEffect, useId, useMemo, useState } from "react";
import type { RealtimeChannel } from "@supabase/supabase-js";
import { useAuthSession } from "../auth/useAuthSession.ts";
import { getBrowserClient } from "../supabase/browser.ts";

export interface UserSlipLeg {
  signal_id: string;
  fixture_name: string;
  market: string;
  selection: string;
  odds: number;
  league: string | null;
  kickoff_at: string;
  tier: "tier_a" | "tier_b" | "tier_c";
}

export type SlipStatus = "building" | "locked" | "settled";
export type SlipOutcome = "won" | "lost" | "partial" | "void" | null;

export interface UserSlip {
  slip_id: string;
  user_id: string;
  slip_date: string;
  legs: UserSlipLeg[];
  legs_total: number;
  combined_odds: number | null;
  stake_amount: number | null;
  potential_return: number | null;
  status: SlipStatus;
  locked_at: string | null;
  settled_at: string | null;
  outcome: SlipOutcome;
}

export interface LockSlipInput {
  legs: UserSlipLeg[];
  combinedOdds: number;
  stakeAmount?: number | null;
}

export interface UseUserSlipsState {
  slips: UserSlip[];
  loading: boolean;
  locking: boolean;
  error: string | null;
  lockSlip: (input: LockSlipInput) => Promise<{ ok: true; slip: UserSlip } | { ok: false; error: string }>;
  refetch: () => Promise<void>;
}

function asLeg(v: unknown): UserSlipLeg | null {
  if (!v || typeof v !== "object") return null;
  const r = v as Record<string, unknown>;
  const signal_id = typeof r["signal_id"] === "string" ? r["signal_id"] : null;
  const fixture_name = typeof r["fixture_name"] === "string" ? r["fixture_name"] : null;
  const market = typeof r["market"] === "string" ? r["market"] : null;
  const selection = typeof r["selection"] === "string" ? r["selection"] : null;
  const oddsRaw = r["odds"];
  const odds =
    typeof oddsRaw === "number"
      ? oddsRaw
      : typeof oddsRaw === "string"
        ? Number(oddsRaw)
        : NaN;
  const kickoff_at = typeof r["kickoff_at"] === "string" ? r["kickoff_at"] : null;
  const tier = r["tier"];
  if (
    !signal_id ||
    !fixture_name ||
    !market ||
    !selection ||
    !kickoff_at ||
    !Number.isFinite(odds) ||
    (tier !== "tier_a" && tier !== "tier_b" && tier !== "tier_c")
  ) {
    return null;
  }
  return {
    signal_id,
    fixture_name,
    market,
    selection,
    odds,
    league: typeof r["league"] === "string" ? r["league"] : null,
    kickoff_at,
    tier,
  };
}

function asNumOrNull(v: unknown): number | null {
  if (v === null || v === undefined) return null;
  if (typeof v === "number") return Number.isFinite(v) ? v : null;
  if (typeof v === "string") {
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

function asStatus(v: unknown): SlipStatus {
  return v === "locked" || v === "settled" ? v : "building";
}

function asOutcome(v: unknown): SlipOutcome {
  return v === "won" || v === "lost" || v === "partial" || v === "void" ? v : null;
}

/** Pure normalizer — exported for testing. */
export function normaliseSlip(row: Record<string, unknown>): UserSlip | null {
  const slip_id = typeof row["slip_id"] === "string" ? row["slip_id"] : null;
  const user_id = typeof row["user_id"] === "string" ? row["user_id"] : null;
  const slip_date = typeof row["slip_date"] === "string" ? row["slip_date"] : null;
  if (!slip_id || !user_id || !slip_date) return null;

  const rawLegs = Array.isArray(row["legs"]) ? (row["legs"] as unknown[]) : [];
  const legs: UserSlipLeg[] = [];
  for (const r of rawLegs) {
    const leg = asLeg(r);
    if (leg) legs.push(leg);
  }

  return {
    slip_id,
    user_id,
    slip_date,
    legs,
    legs_total:
      typeof row["legs_total"] === "number" ? row["legs_total"] : legs.length,
    combined_odds: asNumOrNull(row["combined_odds"]),
    stake_amount: asNumOrNull(row["stake_amount"]),
    potential_return: asNumOrNull(row["potential_return"]),
    status: asStatus(row["status"]),
    locked_at: typeof row["locked_at"] === "string" ? row["locked_at"] : null,
    settled_at: typeof row["settled_at"] === "string" ? row["settled_at"] : null,
    outcome: asOutcome(row["outcome"]),
  };
}

export function useUserSlips(): UseUserSlipsState {
  const supabase = useMemo(() => getBrowserClient(), []);
  const instanceId = useId();
  const { user } = useAuthSession();
  const userId = user?.id ?? null;
  const [slips, setSlips] = useState<UserSlip[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [locking, setLocking] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const refetch = useCallback(async (): Promise<void> => {
    if (!userId) {
      setSlips([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const sb = supabase as any;
    const res = await sb
      .from("daily_acca_user_slips")
      .select(
        "slip_id, user_id, slip_date, legs, legs_total, combined_odds, stake_amount, potential_return, status, locked_at, settled_at, outcome",
      )
      .eq("user_id", userId)
      .order("locked_at", { ascending: false, nullsFirst: false });

    if (res.error) {
      setError(res.error.message ?? "Failed to load slips");
      setSlips([]);
      setLoading(false);
      return;
    }
    const rows = Array.isArray(res.data) ? (res.data as Array<Record<string, unknown>>) : [];
    const normalised = rows
      .map(normaliseSlip)
      .filter((r): r is UserSlip => r !== null);
    setSlips(normalised);
    setLoading(false);
  }, [supabase, userId]);

  useEffect(() => {
    void refetch();
  }, [refetch]);

  // Realtime: own-uid filter
  useEffect(() => {
    if (!userId) return;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const sb = supabase as any;
    const channel: RealtimeChannel = sb
      .channel(`daily_acca_user_slips:${userId}:${instanceId}`)
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "daily_acca_user_slips",
          filter: `user_id=eq.${userId}`,
        },
        () => {
          void refetch();
        },
      )
      .subscribe();
    return () => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (supabase as any).removeChannel(channel);
    };
  }, [supabase, userId, refetch, instanceId]);

  const lockSlip = useCallback(
    async (
      input: LockSlipInput,
    ): Promise<{ ok: true; slip: UserSlip } | { ok: false; error: string }> => {
      if (!userId) {
        return { ok: false, error: "auth-required" };
      }
      if (!input.legs.length) {
        return { ok: false, error: "no-legs" };
      }
      setLocking(true);
      setError(null);
      const stake =
        typeof input.stakeAmount === "number" && Number.isFinite(input.stakeAmount) && input.stakeAmount > 0
          ? input.stakeAmount
          : null;
      const potential = stake !== null ? Number((stake * input.combinedOdds).toFixed(2)) : null;
      const slipDateLagos = new Date()
        .toLocaleDateString("en-CA", { timeZone: "Africa/Lagos" });
      const payload = {
        user_id: userId,
        slip_date: slipDateLagos,
        legs: input.legs,
        legs_total: input.legs.length,
        legs_pending: input.legs.length,
        combined_odds: Number(input.combinedOdds.toFixed(4)),
        stake_amount: stake,
        potential_return: potential,
        is_full_menu: false,
        status: "locked" as const,
        locked_at: new Date().toISOString(),
      };
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const sb = supabase as any;
      const res = await sb
        .from("daily_acca_user_slips")
        .insert(payload)
        .select(
          "slip_id, user_id, slip_date, legs, legs_total, combined_odds, stake_amount, potential_return, status, locked_at, settled_at, outcome",
        )
        .single();
      setLocking(false);
      if (res.error) {
        const msg = res.error.message ?? "Failed to lock slip";
        setError(msg);
        return { ok: false, error: msg };
      }
      const slip = normaliseSlip(res.data as Record<string, unknown>);
      if (!slip) {
        return { ok: false, error: "Lock returned an invalid row" };
      }
      setSlips((prev) => [slip, ...prev.filter((s) => s.slip_id !== slip.slip_id)]);
      return { ok: true, slip };
    },
    [supabase, userId],
  );

  return useMemo(
    () => ({ slips, loading, locking, error, lockSlip, refetch }),
    [slips, loading, locking, error, lockSlip, refetch],
  );
}
